import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";

const emptyForm = {
  first_name: "",
  middle_name: "",
  last_name: "",
  gender: "",
  date_of_birth: "",
  phone: "",
  email: "",
  address: "",
  status: "ACTIVE",
  is_active: true,
  current_class: "",
  stream: "",
  current_class_group: "",
  enrollment_date: "",
  previous_school: "",
  medical_conditions: "",
  allergies: "",
  emergency_contact_name: "",
  emergency_contact_phone: "",
};

function StudentEditModal({ student, onClose, onUpdated }) {
  const [formData, setFormData] = useState(emptyForm);
  const [classGroups, setClassGroups] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadClassGroups = async () => {
      try {
        const response = await axiosInstance.get("academic/class-groups/");
        setClassGroups(response.data || []);
      } catch (error) {
        console.error(error);
        toast.error("Could not load academic classes.");
      }
    };

    loadClassGroups();
  }, []);

  useEffect(() => {
    if (!student) return;

    setFormData({
      first_name: student.first_name || "",
      middle_name: student.middle_name || "",
      last_name: student.last_name || "",
      gender: student.gender || "",
      date_of_birth: student.date_of_birth || "",
      phone: student.phone || "",
      email: student.email || "",
      address: student.address || "",
      status: student.is_active ? student.status || "ACTIVE" : "INACTIVE",
      is_active: Boolean(student.is_active),
      current_class: student.current_class || "",
      stream: student.stream || "",
      current_class_group: student.current_class_group || "",
      enrollment_date: student.enrollment_date || "",
      previous_school: student.previous_school || "",
      medical_conditions: student.medical_conditions || "",
      allergies: student.allergies || "",
      emergency_contact_name: student.emergency_contact_name || "",
      emergency_contact_phone: student.emergency_contact_phone || "",
    });
  }, [student]);

  const selectedClassGroup = useMemo(
    () => classGroups.find((group) => String(group.id) === String(formData.current_class_group)),
    [classGroups, formData.current_class_group]
  );

  if (!student) return null;

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((prev) => {
      const nextForm = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };

      if (name === "is_active" && !checked) {
        nextForm.status = "INACTIVE";
      }

      if (name === "is_active" && checked && prev.status === "INACTIVE") {
        nextForm.status = "ACTIVE";
      }

      if (name === "status") {
        nextForm.is_active = value !== "INACTIVE";
      }

      return nextForm;
    });
  };

  const handleClassGroupChange = (event) => {
    const groupId = event.target.value;
    const group = classGroups.find((item) => String(item.id) === String(groupId));

    setFormData((prev) => ({
      ...prev,
      current_class_group: groupId,
      current_class: group?.class_name || group?.display_name || "",
      stream: group?.stream_name || "",
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      toast.warning("First name and last name are required.");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        ...formData,
        current_class_group: formData.current_class_group || null,
        status:
          formData.is_active && formData.status === "INACTIVE"
            ? "ACTIVE"
            : formData.is_active
            ? formData.status
            : "INACTIVE",
        is_active: formData.status !== "INACTIVE" && formData.is_active,
      };

      const response = await axiosInstance.put(`students/${student.id}/`, payload);
      toast.success("Student updated successfully.");
      onUpdated(response.data);
    } catch (error) {
      console.error(error);
      const data = error.response?.data;
      const message =
        data?.detail ||
        Object.values(data || {})?.flat?.()?.[0] ||
        "Failed to update student.";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ background: "rgba(0,0,0,0.45)" }}
    >
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content border-0 shadow">
          <div className="modal-header">
            <div>
              <h5 className="modal-title fw-bold">Edit Student</h5>
              <small className="text-muted">{student.student_number}</small>
            </div>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">First Name</label>
                  <input name="first_name" className="form-control" value={formData.first_name} onChange={handleChange} required />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Middle Name</label>
                  <input name="middle_name" className="form-control" value={formData.middle_name} onChange={handleChange} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Last Name</label>
                  <input name="last_name" className="form-control" value={formData.last_name} onChange={handleChange} required />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Gender</label>
                  <select name="gender" className="form-select" value={formData.gender} onChange={handleChange}>
                    <option value="">Select</option>
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Date of Birth</label>
                  <input type="date" name="date_of_birth" className="form-control" value={formData.date_of_birth} onChange={handleChange} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Class Group</label>
                  <select
                    className="form-select"
                    value={formData.current_class_group}
                    onChange={handleClassGroupChange}
                  >
                    <option value="">Select class group</option>
                    {classGroups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.academic_year_name} - {group.display_name}
                        {group.class_teacher_name ? ` (${group.class_teacher_name})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Class</label>
                  <input className="form-control" value={formData.current_class} readOnly />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Stream</label>
                  <input className="form-control" value={formData.stream} readOnly />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Academic Year</label>
                  <input className="form-control" value={selectedClassGroup?.academic_year_name || ""} readOnly />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Class Teacher</label>
                  <input className="form-control" value={selectedClassGroup?.class_teacher_name || "Not assigned"} readOnly />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Enrollment Date</label>
                  <input type="date" name="enrollment_date" className="form-control" value={formData.enrollment_date} onChange={handleChange} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Phone</label>
                  <input name="phone" className="form-control" value={formData.phone} onChange={handleChange} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Email</label>
                  <input type="email" name="email" className="form-control" value={formData.email} onChange={handleChange} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Status</label>
                  <select name="status" className="form-select" value={formData.status} onChange={handleChange}>
                    <option value="ACTIVE">Active</option>
                    <option value="PENDING">Pending</option>
                    <option value="SUSPENDED">Suspended</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                </div>
                <div className="col-md-6 d-flex align-items-end">
                  <label className="form-check mb-2">
                    <input type="checkbox" name="is_active" className="form-check-input" checked={formData.is_active} onChange={handleChange} />
                    <span className="form-check-label">Student account is active</span>
                  </label>
                </div>
                <div className="col-12">
                  <label className="form-label">Address</label>
                  <textarea name="address" rows="2" className="form-control" value={formData.address} onChange={handleChange}></textarea>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Emergency Contact Name</label>
                  <input name="emergency_contact_name" className="form-control" value={formData.emergency_contact_name} onChange={handleChange} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Emergency Contact Phone</label>
                  <input name="emergency_contact_phone" className="form-control" value={formData.emergency_contact_phone} onChange={handleChange} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Medical Conditions</label>
                  <textarea name="medical_conditions" rows="3" className="form-control" value={formData.medical_conditions} onChange={handleChange}></textarea>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Allergies</label>
                  <textarea name="allergies" rows="3" className="form-control" value={formData.allergies} onChange={handleChange}></textarea>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-light" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-success" disabled={saving}>
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default StudentEditModal;
